import { isUuid } from '../../utils/format';

/**
 * Originator package detail path. Batch and package share the same UUID in
 * GET /v1/batches and POST /v1/packages; skip the link when the id is not a UUID
 * so a display reference cannot send the user to a broken route.
 */
export function originatorPackagePath(batchId) {
  const id = batchId == null ? '' : String(batchId);
  return isUuid(id) ? `/packages/${id}` : null;
}

/**
 * Package Assets view — loan table that opens Digitize.
 */
export function originatorPackageAssetsPath(batchId) {
  const id = batchId == null ? '' : String(batchId);
  return isUuid(id) ? `/packages/${id}?view=assets` : null;
}

export function originatorPackageLoansPath(batchId) {
  const id = batchId == null ? '' : String(batchId);
  if (!isUuid(id)) return '/loans?tab=loans';
  return `/loans?tab=loans&batch=${encodeURIComponent(id)}`;
}

/**
 * Originator-facing next action. Certification creates a Provider ReviewTask;
 * it is not authority approval, custody acceptance, or vault admission.
 *
 * Backend batch payloads still return next_action_owner=custodian once
 * upload_status is COMPLETE. That value is not used as custody-ready.
 * ReviewTask/CANDIDATE/ROUTED are not on this payload, so CERTIFIED +
 * authority PENDING is treated as Provider review — not Custodian.
 */
export function originatorNextAction(batch, admission) {
  if (!batch) {
    return { owner: 'originator', label: 'With you', tone: 'warning' };
  }
  if (batch.custody_status === 'ACCEPTED') {
    return { owner: 'admitted', label: 'Admitted', tone: 'success' };
  }
  if (batch.authority_status === 'APPROVED') {
    return { owner: 'authority', label: 'Authority approved', tone: 'success' };
  }
  const readiness = admission?.readiness_status;
  if (readiness && readiness !== 'CERTIFIED') {
    if (readiness === 'BLOCKED') {
      return { owner: 'originator', label: 'Cure admission exception', tone: 'danger' };
    }
    if (readiness === 'RETURNED') {
      return { owner: 'originator', label: 'Cure returned facts', tone: 'warning' };
    }
    if (readiness === 'REVIEW_REQUIRED') {
      return { owner: 'originator', label: 'Confirm admission details', tone: 'warning' };
    }
    if (readiness === 'READY_TO_CERTIFY') {
      return { owner: 'originator', label: 'Certify package', tone: 'info' };
    }
    if (readiness === 'EVALUATING') {
      return {
        owner: 'originator',
        label: 'Processing admission checks…',
        tone: 'info',
      };
    }
    if (readiness === 'NOT_EVALUATED') {
      return { owner: 'originator', label: 'Run admission checks', tone: 'warning' };
    }
  }
  if (batch.package_status === 'CERTIFIED' || readiness === 'CERTIFIED') {
    return {
      owner: 'provider',
      label: 'Submitted for Provider review',
      tone: 'info',
    };
  }
  return { owner: 'originator', label: 'With you', tone: 'warning' };
}
